import React, { useCallback, useEffect, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import PropTypes from "prop-types";
import { scanAddressService } from "../../services/ocr.service";
import LoaderSpinner from "../common/Loader";
import { formatScannedAddress } from "./scanAddressMapper";
import "./ScanAddressModal.css";

export const MAX_IMAGE_BYTES = 10 * 1024 * 1024;
export const READ_FAILED_MESSAGE = "Unable to read the file. Please try again.";

const isPdf = (file) => file?.type === "application/pdf";
const isSupported = (file) => file?.type?.startsWith("image/") || isPdf(file);

const getErrorMessage = (error) => {
  const data = error?.response?.data;
  if (typeof data === "string" && data) return data;
  return data?.Error || data?.error || READ_FAILED_MESSAGE;
};

// The API serializer may return PascalCase or camelCase property names.
const readField = (source, key) => source?.[key] ?? source?.[key.charAt(0).toUpperCase() + key.slice(1)] ?? "";

export const readScannedAddresses = (data) =>
  (data?.addresses ?? data?.Addresses ?? []).map((item) => ({
    label: readField(item, "label") || null,
    name: readField(item, "name"),
    addressLine1: readField(item, "addressLine1"),
    addressLine2: readField(item, "addressLine2"),
    city: readField(item, "city"),
    state: readField(item, "state"),
    zip: readField(item, "zip"),
  }));

const ScanAddressModal = ({ modalShow, addressType, handleClose, onApply }) => {
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [candidates, setCandidates] = useState([]);
  const [selected, setSelected] = useState(0);
  const inputRef = useRef(null);
  const activeRequest = useRef(0);

  const title = `Scan Image – ${addressType === "sender" ? "Sender" : "Receiver"}`;

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  useEffect(() => {
    return () => {
      activeRequest.current += 1;
    };
  }, []);

  const resetResult = () => {
    activeRequest.current += 1;
    setCandidates([]);
    setSelected(0);
    setError("");
    setLoading(false);
  };

  const selectFile = useCallback((next) => {
    if (!next) return;
    if (!isSupported(next)) {
      setError("Please select an image or PDF file (JPG, PNG or PDF).");
      return;
    }
    if (next.size > MAX_IMAGE_BYTES) {
      setError("File exceeds the 10 MB limit.");
      return;
    }
    activeRequest.current += 1;
    setCandidates([]);
    setSelected(0);
    setError("");
    setLoading(false);
    setFile(next);
    setPreviewUrl(isPdf(next) ? null : URL.createObjectURL(next));
  }, []);

  useEffect(() => {
    if (!modalShow) return undefined;
    const onPaste = (event) => {
      const item = Array.from(event.clipboardData?.items || []).find(
        (entry) => entry.kind === "file" && entry.type.startsWith("image/")
      );
      if (item) {
        event.preventDefault();
        selectFile(item.getAsFile());
      }
    };
    document.addEventListener("paste", onPaste);
    return () => document.removeEventListener("paste", onPaste);
  }, [modalShow, selectFile]);

  const removeFile = () => {
    resetResult();
    setFile(null);
    setPreviewUrl(null);
    if (inputRef.current) inputRef.current.value = "";
  };

  const onDrop = (event) => {
    event.preventDefault();
    setDragOver(false);
    selectFile(event.dataTransfer?.files?.[0]);
  };

  const onDragOver = (event) => {
    event.preventDefault();
    setDragOver(true);
  };

  const extract = async () => {
    if (!file || loading) return;
    const requestId = activeRequest.current + 1;
    activeRequest.current = requestId;
    setLoading(true);
    setError("");
    try {
      const response = await scanAddressService(file, addressType);
      if (activeRequest.current !== requestId) return;
      const addresses = readScannedAddresses(response?.data);
      if (addresses.length === 0) {
        setError(READ_FAILED_MESSAGE);
      } else {
        setCandidates(addresses);
        setSelected(0);
      }
    } catch (requestError) {
      if (activeRequest.current !== requestId) return;
      setError(getErrorMessage(requestError));
    } finally {
      if (activeRequest.current === requestId) setLoading(false);
    }
  };

  const hasResult = candidates.length > 0;
  const hasChoices = candidates.length > 1;
  const submit = () => (hasResult ? onApply(candidates[selected]) : extract());

  return (
    <Modal className="scanAddressModal" size="lg" show={modalShow} onHide={handleClose}>
      <Modal.Header>
        <div className="modal-main-div">
          <div className="modal-m-text">{title}</div>
        </div>
        <div className="modalDeleteWrapper">
          <button type="button" className="btn-close" aria-label="Close" onClick={handleClose}></button>
        </div>
      </Modal.Header>
      <Modal.Body>
        {loading && <LoaderSpinner />}
        <button
          type="button"
          className={`scanDrop ${dragOver ? "hover" : ""}`}
          onClick={() => inputRef.current?.click()}
          onDragOver={onDragOver}
          onDragEnter={onDragOver}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          disabled={loading}
          data-testid="scan-address-drop"
        >
          <span className="scanDropTitle">
            Paste a screenshot (Ctrl+V), drag &amp; drop, or <u>browse</u>
          </span>
          <span className="scanDropHint">Packing slip, order screenshot, address photo, label… · JPG, PNG, PDF · Max 10 MB</span>
        </button>
        <input
          ref={inputRef}
          type="file"
          accept="image/*,application/pdf"
          hidden
          data-testid="scan-address-input"
          onChange={(event) => selectFile(event.target.files[0])}
        />

        {file && (
          <div className="scanFile">
            {previewUrl && <img className="scanThumb" src={previewUrl} alt="Selected file preview" />}
            {isPdf(file) && <span className="scanPdfBadge" data-testid="scan-address-pdf-badge">PDF</span>}
            <span className="scanFileName">{file.name}</span>
            <button type="button" className="scanRemove" aria-label="Remove file" onClick={removeFile} disabled={loading}>
              &times;
            </button>
          </div>
        )}

        {error && (
          <div className="scanError" data-testid="scan-address-error">
            {error}
          </div>
        )}

        {hasChoices && (
          <fieldset className="scanChoices">
            <legend>More than one address was found. Choose the one to use.</legend>
            {candidates.map((candidate, index) => (
              <label key={`${candidate.addressLine1}-${candidate.zip}`} className="scanChoice">
                <input
                  type="radio"
                  name="scannedAddress"
                  checked={selected === index}
                  onChange={() => setSelected(index)}
                />
                <span>
                  {candidate.label && <b className="scanChoiceLabel">{candidate.label}: </b>}
                  {formatScannedAddress(candidate)}
                </span>
              </label>
            ))}
          </fieldset>
        )}

        {hasResult && !hasChoices && (
          <div className="scanChoices" data-testid="scan-address-result">
            <div className="scanChoicesTitle">Address found. Check it, then fill the form.</div>
            <div className="scanChoice">
              <span>
                {candidates[0].label && <b className="scanChoiceLabel">{candidates[0].label}: </b>}
                {formatScannedAddress(candidates[0])}
              </span>
            </div>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <button type="button" className="btn scanCancel" onClick={handleClose}>
          Cancel
        </button>
        <button type="button" className="btn cus-seconday-bg-btn" onClick={submit} disabled={!file || loading}>
          {hasResult ? "Fill Address" : "Extract & Fill"}
        </button>
      </Modal.Footer>
    </Modal>
  );
};

ScanAddressModal.propTypes = {
  modalShow: PropTypes.bool.isRequired,
  addressType: PropTypes.oneOf(["sender", "receiver"]).isRequired,
  handleClose: PropTypes.func.isRequired,
  onApply: PropTypes.func.isRequired,
};

export default ScanAddressModal;
